



import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { fetchFileStorageOnce } from "../store/fileStorageActions";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { getFirestore, collection, addDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { app } from "../firebase";
import { Download, Trash2, Eye } from "lucide-react";

const FileUploadArea = React.memo(({ folderId, folderName,isLocked, readonlyFolderName }) => {
// PropTypes for prop validation
FileUploadArea.propTypes = {
  folderId: PropTypes.string,
  folderName: PropTypes.string,
  isLocked: PropTypes.bool,
  readonlyFolderName: PropTypes.bool
};
  const dispatch = useDispatch();
  const files = useSelector((state) =>
    state.fileStorage && state.fileStorage.files
      ? state.fileStorage.files.filter(f => f.folderId === folderId)
      : []
  );

  // Refresh files when folderId or folderName changes
  useEffect(() => {
    if (folderId) {
      dispatch(fetchFileStorageOnce());
    }
    // eslint-disable-next-line
  }, [folderId, folderName]);
  const db = getFirestore(app);
  const storage = getStorage(app);
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Upload files using Firebase logic
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles.length || !folderId) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      // Fetch all files in the database to check for duplicate names globally
      const allFilesSnapshot = await fetchFileStorageOnce();
      let allFiles = [];
      if (allFilesSnapshot && allFilesSnapshot.payload && Array.isArray(allFilesSnapshot.payload)) {
        allFiles = allFilesSnapshot.payload;
      } else if (Array.isArray(files)) {
        allFiles = files;
      }
      for (const file of selectedFiles) {
        // Check for duplicate file names in the entire database
        let baseName = file.name;
        let ext = '';
        const dotIdx = baseName.lastIndexOf('.');
        if (dotIdx !== -1) {
          ext = baseName.slice(dotIdx);
          baseName = baseName.slice(0, dotIdx);
        }
        let newName = file.name;
        let counter = 1;
        const existingNames = allFiles.map(f => f.name);
        while (existingNames.includes(newName)) {
          newName = `${baseName}(${counter})${ext}`;
          counter++;
        }
        // If the name was changed, append the new name to the file content (e.g., add to metadata or as a property)
        let fileToUpload;
        if (newName !== file.name) {
          // Append the new name to the file content as a comment (for text files) or as metadata
          // For simplicity, add a custom property to the File object
          fileToUpload = new File([file], newName, { type: file.type });
          fileToUpload.originalName = file.name;
          fileToUpload.renamedName = newName;
        } else {
          fileToUpload = file;
        }
        const storageRef = ref(storage, `fileStorage/${folderId}/${fileToUpload.name}`);
        const uploadTask = uploadBytesResumable(storageRef, fileToUpload);
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            null,
            reject,
            async () => {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              const now = Timestamp.now ? Timestamp.now() : new Date();
              await addDoc(collection(db, "fileStorages"), {
                name: fileToUpload.name,
                folderId,
                downloadUrl,
                type: file.type,
                size: uploadTask.snapshot.totalBytes || file.size || 0,
                createdAt: now,
                updatedAt: now,
                modifiedAt: now,
                originalName: fileToUpload.originalName || file.name,
                renamedName: fileToUpload.renamedName || null,
              });
              resolve();
            }
          );
        });
        // Add the new name to allFiles to prevent further duplicates in this batch
        allFiles.push({ name: fileToUpload.name });
      }
      setSuccess("Files uploaded!");
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await dispatch(fetchFileStorageOnce());
    } catch (err) {
      setError("Error uploading file(s): " + (err && err.message ? err.message : String(err)));
    }
    setLoading(false);
  };

  // Delete file using Firebase logic
  const handleDelete = async (file) => {
    setLoading(true);
    setError("");
    try {
      const fileRef = ref(storage, `fileStorage/${file.folderId}/${file.name}`);
      await deleteObject(fileRef);
      await deleteDoc(doc(db, "fileStorages", file.id));
      setSuccess("File deleted!");
      await dispatch(fetchFileStorageOnce());
    } catch (err) {
      setError("Error deleting file: " + (err && err.message ? err.message : String(err)));
    }
    setLoading(false);
  };

  // Download file
  const handleDownload = async (file) => {
    setLoading(true);
    try {
      const response = await fetch(file.downloadUrl);
      if (!response.ok) throw new Error("Failed to download file");
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError("Error downloading file: " + (err && err.message ? err.message : String(err)));
    }
    setLoading(false);
  };

  // Manual refresh
  const handleRefresh = () => {
    dispatch(fetchFileStorageOnce());
  };

  // Rename file logic

  return (
    <div className="mt-4 w-full max-w-2xl mx-auto px-2 sm:px-4" >
      <div className="flex flex-col gap-2 mb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
          <h3 className="text-lg font-semibold truncate">Files in "{folderName}"</h3>
          <button
            className="mt-1 sm:mt-0 px-4 py-2 bg-[#0071bd] text-white rounded-lg font-semibold shadow hover:bg-[#005fa3] transition-all text-sm w-full sm:w-auto"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh file list"
          >
            Refresh
          </button>
        </div>
        {!readonlyFolderName && (
          <form onSubmit={handleUpload} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={e => setSelectedFiles(Array.from(e.target.files))}
              className="border rounded px-2 py-1 w-full sm:w-auto"
              disabled={loading}
            />
            <button
              type="submit"
              className="px-4 py-2 bg-[#0071bd] text-white rounded-lg font-semibold shadow hover:bg-[#005fa3] transition-all text-sm w-full sm:w-auto"
              disabled={loading || !selectedFiles.length}
            >
              Upload
            </button>
          </form>
        )}
      </div>
      {success && <div className="mb-2 p-2 bg-green-100 text-green-700 rounded text-center text-sm">{success}</div>}
      {error && <div className="mb-2 p-2 bg-red-100 text-red-700 rounded text-center text-sm">{error}</div>}
      <ul className="divide-y divide-gray-200">
        {!isLocked && files.map((file,i) => (
          <li key={file.id+""+i+file.name} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 group hover:bg-blue-50 rounded transition w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-3 min-w-0 w-full">
              <span className="truncate font-medium text-gray-800 cursor-pointer hover:underline max-w-full" title={file.name} onClick={() => window.open(file.downloadUrl, '_blank')}>{file.name}</span>
              <div className="flex gap-2 flex-wrap mt-1 sm:mt-0">
                <span className="text-xs text-gray-500">{file.size ? `${(Number(file.size)/1024).toFixed(1)} KB` : ''}</span>
                <span className="text-xs text-gray-500">{file.uploadedAt && file.uploadedAt.seconds ? new Date(file.uploadedAt.seconds * 1000).toLocaleString() : ''}</span>
              </div>
            </div>
            <div className="flex flex-row gap-2 items-center mt-2 sm:mt-0 w-full sm:w-auto justify-end">
              <button className="text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg p-2 transition-all shadow flex items-center" title="Download" onClick={() => handleDownload(file)}>
                <Download size={18} />
              </button>
              <button className="text-green-600 bg-green-50 hover:bg-green-100 rounded-lg p-2 transition-all shadow flex items-center" title="Preview" onClick={() => window.open(file.downloadUrl, '_blank')}>
                <Eye size={18} />
              </button>
              {!readonlyFolderName && (
                <button className="text-yellow-700 bg-yellow-50 hover:bg-yellow-100 rounded-lg p-2 transition-all shadow flex items-center font-semibold" title="Rename" onClick={() => handleRename(file)}>
                  <span className="font-bold text-xs">Rename</span>
                </button>
              )}
              {!readonlyFolderName && (
                <button className="text-red-600 bg-red-50 hover:bg-red-100 rounded-lg p-2 transition-all shadow flex items-center" title="Delete" onClick={() => handleDelete(file)}>
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          </li>
        ))}
        {files.length === 0 && <li className="text-gray-500 text-sm text-center py-4">No files uploaded yet.</li>}
      </ul>
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
            <div className="text-blue-700 font-semibold">Processing...</div>
          </div>
        </div>
      )}
    </div>
  );
});

export default FileUploadArea;
